"use client"

import { Pressable } from "react-native"
import { FormattedMessage } from "react-intl"

import {
  Box,
  H4,
  Icon,
  P,
  Sheet,
  useAppConfig,
  useCopyToClipboard,
  useLoggingContext,
} from "@bullseye/components"
import type { Modal } from "@bullseye/types"

function makeFBMessengerShareLink(url?: string, appId?: string) {
  if (!appId || !url) return ""

  const link = encodeURIComponent(url)

  const messengerLink = new URL("https://www.facebook.com/dialog/send")
  messengerLink.searchParams.append("app_id", appId)
  messengerLink.searchParams.append("href", link)
  messengerLink.searchParams.append("display", "popup")
  messengerLink.searchParams.append("redirect_uri", link)

  return messengerLink.toString()
}

function makeSMSLink(message?: string, url?: string) {
  if (!message && !url) return ""
  const msg = message || ""
  const u = url || ""
  const body = msg.includes(u) ? msg : `${msg}\n${u}`
  return `sms:?&body=${encodeURIComponent(body)}`
}

function makeEmailShareLink(url?: string, subject?: string, body?: string) {
  const params = [
    `subject=${encodeURIComponent(subject || "")}`,
    `body=${encodeURIComponent(body || url || "")}`,
  ]
  return `mailto:?${params.join("&")}`
}

export type ShareModalProps = {
  onCopy: (success: boolean) => void

  url: string
  message: string
  emailSubject: string
}

export const ShareModal = ({
  isOpen,
  dismiss,
  onCopy,
  url,
  message,
  emailSubject,
}: Modal<ShareModalProps>) => {
  const { facebookAppId } = useAppConfig()
  const messengerShareLink = makeFBMessengerShareLink(url, facebookAppId)
  const smsLink = makeSMSLink(message, url)
  const emailShareLink = makeEmailShareLink(url, emailSubject, message)
  const { copy } = useCopyToClipboard()
  const { captureException } = useLoggingContext()

  const titleText = message?.split("\n")[0] || undefined

  return (
    <Sheet
      icon="x"
      show={isOpen}
      onIconPress={() => dismiss()}
      dismiss={dismiss}
      renderContent={() => {
        return (
          <Box className="flex items-center g-6">
            {titleText && <H4 className="text-center">{titleText}</H4>}
            <Box className="flex-row justify-center g-8">
              {false && messengerShareLink ? (
                <Pressable
                  onPress={() => {
                    if (typeof window !== "undefined") {
                      window.open(messengerShareLink, "_blank")
                    }
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="Share via Messenger"
                >
                  <Box className="items-center g-2">
                    <Box className="h-16 w-16 items-center justify-center rounded-2xl bg-gray-100">
                      <Icon type="share-messenger" size="xl" />
                    </Box>
                    <P>Messenger</P>
                  </Box>
                </Pressable>
              ) : null}
              <Pressable
                onPress={() => {
                  if (typeof window !== "undefined") {
                    window.open(smsLink, "_blank")
                  }
                }}
                accessibilityRole="button"
                accessibilityLabel="Share via SMS"
              >
                <Box className="items-center g-2">
                  <Box className="h-16 w-16 items-center justify-center rounded-2xl bg-gray-100">
                    <Icon type="share-sms" size="xl" />
                  </Box>
                  <P>SMS</P>
                </Box>
              </Pressable>
              <Pressable
                onPress={() => {
                  if (typeof window !== "undefined") {
                    window.open(emailShareLink, "_self")
                  }
                }}
                accessibilityRole="button"
                accessibilityLabel="Share via email"
              >
                <Box className="items-center g-2">
                  <Box className="h-16 w-16 items-center justify-center rounded-2xl bg-gray-100">
                    <Icon type="share-email" size="xl" />
                  </Box>
                  <P>Email</P>
                </Box>
              </Pressable>
            </Box>
            <Box className="w-full g-2">
              <P bold>
                <FormattedMessage id="activity.share.inviteLink" />
              </P>
              <Pressable
                onPress={async () => {
                  try {
                    const copied = await copy(url)
                    onCopy(copied)
                  } catch (error) {
                    captureException(
                      new Error(`Failed to copy to clipboard: ${error}`),
                    )
                    onCopy(false)
                  }
                }}
                accessibilityRole="button"
                accessibilityLabel="Copy link"
              >
                <Box className="flex-row items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
                  <P
                    className="flex-1 truncate text-gray-500"
                    numberOfLines={1}
                  >
                    {url}
                  </P>
                  <Box className="ml-2">
                    <Icon type="link" size="lg" />
                  </Box>
                </Box>
              </Pressable>
            </Box>
          </Box>
        )
      }}
    />
  )
}
